import React, { Component } from 'react'
import { StyleSheet, Text, View } from 'react-native'
import styles, { colors } from '../../styles/global'
import Backend from '../../../utils/BackendBridge'
import SocialTextInput from '../../SocialTextInput'
import store from '../../../redux/store'
import { setTaskTitleInEditMode } from '../../../redux/actions'
import { DV_TAB_TASK_CHAT, DV_TAB_TASK_NOTE } from '../../../utils/TabNavigationConstants'
import ProjectHelper from '../../SettingsView/ProjectsSettings/ProjectHelper'
import { TASK_ASSIGNEE_ASSISTANT_TYPE } from '../../TaskListView/Utils/TasksHelper'
import { setTaskName } from '../../../utils/backends/Tasks/tasksFirestore'
import { isGmailLabelFollowUpTask } from '../../../utils/Gmail/gmailTaskUtils'
import GmailTag from '../../Tags/GmailTag'

export const TITLE_TASK = 0
export const TITLE_NOTE = 2

// The collapsed title height is expressed in lines instead of a magic pixel value, because the
// heading has to fit a very different amount of text on a phone than on a desktop (AT-2341).
// A wrapped title line is `styles.title4`'s lineHeight plus the 3px top/bottom margins that
// WordsList adds to every wrapped word, and the whole title block also carries the fixed-height
// spacer above the text.
export const TITLE_UPPER_SPACER_HEIGHT = 32
export const TITLE_LINE_HEIGHT = styles.title4.lineHeight + 6
export const COLLAPSED_TITLE_LINES_DESKTOP = 1
export const COLLAPSED_TITLE_LINES_MOBILE = 3
export const EXPANDED_TITLE_MAX_HEIGHT = 800

export const getCollapsedTitleMaxHeight = mobile =>
    TITLE_UPPER_SPACER_HEIGHT +
    TITLE_LINE_HEIGHT * (mobile ? COLLAPSED_TITLE_LINES_MOBILE : COLLAPSED_TITLE_LINES_DESKTOP)

class TaskTitle extends Component {
    constructor(props) {
        super(props)
        const storeState = store.getState()

        this._isMounted = false

        this.state = {
            taskTitle: this.props.title,
            selectedTab: storeState.selectedNavItem,
            taskTitleInEditMode: storeState.taskTitleInEditMode,
            mobile: storeState.smallScreenNavigation,
        }
    }

    componentDidMount() {
        this._isMounted = true
        // Subscribe here rather than in the constructor so the subscription's lifetime
        // matches the mount lifetime. Redux notifies a snapshot of its listener list, so a
        // dispatch that unmounts this view would otherwise still reach a listener registered
        // for an instance that is already gone.
        this.unsubscribe = store.subscribe(this.updateState)
        // Catch up on any store change that landed between construction and mount.
        this.updateState()
    }

    getMaxHeight = () => {
        const { taskTitleInEditMode, selectedTab, mobile } = this.state
        const isCollapsedTab = selectedTab === DV_TAB_TASK_CHAT || selectedTab === DV_TAB_TASK_NOTE
        return isCollapsedTab && !taskTitleInEditMode ? getCollapsedTitleMaxHeight(mobile) : EXPANDED_TITLE_MAX_HEIGHT
    }

    componentDidUpdate(prevProps) {
        const { title } = this.props
        if (prevProps.title !== title && this._isMounted) {
            this.setState({ taskTitle: title })
        }
    }

    componentWillUnmount() {
        this._isMounted = false
        if (this.unsubscribe) {
            this.unsubscribe()
            this.unsubscribe = null
        }
    }

    onTitleLayoutChange = ({ nativeEvent }) => {
        const maxHeight = this.getMaxHeight()
        const { layout } = nativeEvent

        if (!this._isMounted) {
            return
        }

        if (layout.height > maxHeight && !this.state.showEllipsis) {
            this.setState({ showEllipsis: true })
        } else if (layout.height <= maxHeight && this.state.showEllipsis) {
            this.setState({ showEllipsis: false })
        }
    }

    render() {
        const { taskTitleInEditMode, taskTitle, showEllipsis } = this.state
        const { projectId, task, titleType, onSubmit, object, numberOfLines } = this.props
        const { uid } = store.getState().loggedUser
        const maxHeight = this.getMaxHeight()

        const loggedUserIsTaskOwner = task.userId === uid
        const loggedUserCanUpdateObject =
            loggedUserIsTaskOwner || !ProjectHelper.checkIfLoggedUserIsNormalUserInGuide(projectId)

        const isAssistant = task.assigneeType === TASK_ASSIGNEE_ASSISTANT_TYPE

        // The type chip leads the title from *inside* the wrapping text flow (same as the task
        // list does via SocialText's leftCustomElement). Rendering it as a sibling of the title
        // instead gave it its own full-height column, so every wrapped line of the name stayed
        // trapped in the remaining width and long names truncated almost immediately on mobile.
        const gmailTag = isGmailLabelFollowUpTask(task) ? (
            <GmailTag gmailData={task.gmailData} propStyles={localStyles.gmailTag} showLabel iconSize={12} />
        ) : null

        return (
            <View style={[localStyles.titleContainer, { maxHeight: maxHeight }]}>
                <View style={localStyles.upperContainer} />
                <View style={[localStyles.bottomContainer, { top: taskTitleInEditMode ? -4 : 0 }]}>
                    <View
                        style={{
                            flex: 1,
                            marginRight: taskTitleInEditMode ? 0 : 16,
                        }}
                        onLayout={this.onTitleLayoutChange}
                        pointerEvents={loggedUserCanUpdateObject ? 'auto' : 'none'}
                    >
                        <SocialTextInput
                            key={taskTitle}
                            isFocused={taskTitleInEditMode}
                            onFocus={() => store.dispatch(setTaskTitleInEditMode(true))}
                            value={taskTitle}
                            onSubmitEditing={value => {
                                if (onSubmit != null) {
                                    onSubmit(value)
                                } else {
                                    setTaskName(projectId, task.id, value, task, taskTitle)
                                }
                                if (this._isMounted) {
                                    this.setState({ taskTitle: value })
                                }
                            }}
                            normalStyle={[styles.title4, { color: colors.Text01, whiteSpace: 'pre' }]}
                            hashtagStyle={styles.title6}
                            mentionStyle={styles.title6}
                            emailStyle={styles.title6}
                            linkStyle={styles.title6}
                            projectId={projectId}
                            task={task}
                            inTaskDetailedView={true}
                            titleType={titleType}
                            objectId={object.id}
                            object={object}
                            numberOfLines={numberOfLines}
                            disabled={isAssistant}
                            leftCustomElement={gmailTag}
                        />
                    </View>
                </View>
                {showEllipsis && !taskTitleInEditMode && <Text style={localStyles.ellipsis}>...</Text>}
            </View>
        )
    }

    updateState = () => {
        if (!this._isMounted) {
            return
        }
        const storeState = store.getState()
        const { selectedTab, taskTitleInEditMode, mobile } = this.state
        if (
            selectedTab === storeState.selectedNavItem &&
            taskTitleInEditMode === storeState.taskTitleInEditMode &&
            mobile === storeState.smallScreenNavigation
        ) {
            return
        }
        this.setState({
            selectedTab: storeState.selectedNavItem,
            taskTitleInEditMode: storeState.taskTitleInEditMode,
            mobile: storeState.smallScreenNavigation,
        })
    }
}

TaskTitle.defaultProps = {
    titleType: TITLE_TASK,
}

export default TaskTitle

const localStyles = StyleSheet.create({
    titleContainer: {
        flex: 1,
        minHeight: 64,
        maxHeight: 800,
        overflow: 'hidden',
    },
    upperContainer: {
        height: 32,
        backgroundColor: 'white',
    },
    bottomContainer: {
        flexDirection: 'row',
        backgroundColor: 'white',
        minHeight: 32,
        // maxHeight: 64,
        overflow: 'hidden',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
    },
    gmailTag: {
        // Matches the 3px vertical rhythm WordsList gives every wrapped word, so the chip sits on
        // the first line of the title instead of floating above it.
        marginVertical: 3,
        marginRight: 8,
    },
    ellipsis: {
        ...styles.title4,
        color: colors.Text01,
        backgroundColor: '#ffffff',
        paddingHorizontal: 8,
        position: 'absolute',
        bottom: 0,
        right: 0,
    },
})
